import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiPlus, FiX, FiFolder, FiCheckCircle, FiLoader } from 'react-icons/fi';
import { createSector, updateSector, deleteSector, getSectors, getWhatsAppStatus, getWhatsAppQRCode } from '../services/SectorService';
import SessionService from '../services/SessionService';
import Toast from '../components/Toast';
import { ToastContainer } from '../components/Toast';
import './SectorsPage.css';

interface Sector {
  id?: number;
  name: string;
  userBusinessId?: number;
  phoneNumberId: string;
  accessToken: string;
  description: string;
  googleClientId?: string;
  googleApiKey?: string;
  isOfficial: boolean;
  roleNoSetor?: number; // 1=Colaborador, 2=Admin, 3=SuperAdmin (dono)
  whatsappConnection?: {
    id: number;
    sectorId: number;
    status: string;
    qrcodeBase64: string | null;
    lastQrcodeGeneratedAt: string | null;
    lastConnectedAt: string | null;
    lastDisconnectedAt: string | null;
    sessionData: any;
    createdAt: string;
    updatedAt: string;
  };
}

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface QRCodeData {
  qrcode: string;
  instructions?: string;
}

const SectorsPage: React.FC = () => {
  const canManageSectors = SessionService.canManageSectors();

  const navigate = useNavigate();
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [editingSector, setEditingSector] = useState<Sector | null>(null);
  const [sectorData, setSectorData] = useState<Sector>({
    name: '',
    phoneNumberId: '',
    accessToken: '',
    description: '',
    isOfficial: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [isDeleting, setIsDeleting] = useState<{ [key: number]: boolean }>({});
  const [showQRCode, setShowQRCode] = useState<boolean>(false);
  const [qrCodeData, setQRCodeData] = useState<QRCodeData | null>(null);
  const [qrCodeInstructions, setQrCodeInstructions] = useState<string | null>(null);
  const [whatsappStatus, setWhatsappStatus] = useState<{ status: string; message: string } | null>(null);
  const [selectedSectorForQR, setSelectedSectorForQR] = useState<number | null>(null);
  const [statusPollingInterval, setStatusPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [isLoadingQRCode, setIsLoadingQRCode] = useState<boolean>(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [searchFilter, setSearchFilter] = useState('');
  const [waFilter, setWaFilter] = useState<'all' | 'connected' | 'disconnected'>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchSectors();

    // Cleanup function to clear any existing intervals when component unmounts
    return () => {
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval);
        setStatusPollingInterval(null);
      }
    };
  }, []);

  // Efeito para verificar status inicial dos setores não oficiais
  useEffect(() => {
    const checkUnofficialSectorsStatus = async () => {
      const unofficialSectors = sectors.filter(sector => !sector.isOfficial);
      for (const sector of unofficialSectors) {
        if (sector.id) {
          try {
            const status = await getWhatsAppStatus(sector.id, false);
            setSectors(prev => prev.map(s =>
              s.id === sector.id
                ? {
                  ...s,
                  whatsappConnection: s.whatsappConnection
                    ? { ...s.whatsappConnection, status: status.status }
                    : {
                      id: 0,
                      sectorId: sector.id!,
                      status: status.status,
                      qrcodeBase64: null,
                      lastQrcodeGeneratedAt: null,
                      lastConnectedAt: null,
                      lastDisconnectedAt: null,
                      sessionData: null,
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString()
                    }
                }
                : s
            ));
          } catch (error) {
          }
        }
      }
    };

    // Apenas verificar o status uma vez quando os setores são carregados
    if (sectors.length > 0) {
      checkUnofficialSectorsStatus();
    }
  }, [sectors.length]); // Mudando a dependência para sectors.length

  // Efeito para polling do status quando o modal está aberto
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval);
        setStatusPollingInterval(null);
      }
    };

    if (showQRCode && selectedSectorForQR) {
      const sector = sectors.find(s => s.id === selectedSectorForQR);

      if (sector && !sector.isOfficial) {
        const checkStatus = async () => {
          try {
            const status = await getWhatsAppStatus(selectedSectorForQR, false);
            setWhatsappStatus(status);

            if (status.status === 'connected') {
              setSectors(prev => prev.map(s =>
                s.id === selectedSectorForQR
                  ? {
                    ...s,
                    whatsappConnection: s.whatsappConnection
                      ? { ...s.whatsappConnection, status: status.status }
                      : {
                        id: 0,
                        sectorId: s.id!,
                        status: status.status,
                        qrcodeBase64: null,
                        lastQrcodeGeneratedAt: null,
                        lastConnectedAt: null,
                        lastDisconnectedAt: null,
                        sessionData: null,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString()
                      }
                  }
                  : s
              ));

              // Disparar evento para notificar que o WhatsApp foi conectado
              window.dispatchEvent(new CustomEvent('whatsappConnected', {
                detail: { sectorId: selectedSectorForQR }
              }));

              stopPolling();
              setTimeout(() => {
                setShowQRCode(false);
                setSelectedSectorForQR(null);
                setQRCodeData(null);
              }, 2000);
            }
          } catch (error) {
            stopPolling();
          }
        };

        checkStatus();
        interval = setInterval(checkStatus, 2000);
        setStatusPollingInterval(interval);
      }
    }

    return () => {
      stopPolling();
    };
  }, [showQRCode, selectedSectorForQR]);

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(current => [...current, { id, message, type }]);
    setTimeout(() => removeToast(id), 3000);
  };

  const removeToast = (id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  };

  const fetchSectors = async () => {
    try {
      setIsLoading(true);
      const token = SessionService.getToken();
      if (!token) {
        navigate('/');
        return;
      }
      const response = await getSectors(token);
      setSectors(response || []);
    } catch (error) {
      setSectors([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string | undefined } = {};

    if (!sectorData.name) {
      newErrors.name = 'O nome do setor é obrigatório';
    }
    if (!sectorData.description) {
      newErrors.description = 'A descrição é obrigatória';
    }

    // Validações específicas para API oficial
    if (sectorData.isOfficial) {
      if (!sectorData.phoneNumberId) {
        newErrors.phoneNumberId = 'A identificação do telefone é obrigatória';
      }
      if (!sectorData.accessToken) {
        newErrors.accessToken = 'O token de acesso é obrigatório';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const token = SessionService.getToken();
      if (!token) {
        addToast('Erro de autenticação', 'error');
        return;
      }
      const decodedToken = SessionService.decodeToken(token);
      const userBusinessId = decodedToken?.userId;
      if (!userBusinessId) {
        addToast('Erro de autenticação', 'error');
        return;
      }
      const updatedSectorData = {
        name: sectorData.name,
        description: sectorData.description,
        isOfficial: false,
        phoneNumberId: '',
        accessToken: '',
        userBusinessId
      };
      if (editingSector?.id) {
        try {
          const response = await updateSector(editingSector.id, updatedSectorData);
          if (response) {
            await fetchSectors();
            addToast('Setor atualizado com sucesso', 'success');
            handleCloseDrawer();

            // Disparar evento para notificar que os setores foram atualizados
            window.dispatchEvent(new CustomEvent('sectorsUpdated'));
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          addToast(errorMessage || 'Erro ao atualizar setor', 'error');
        }
      } else {
        try {
          const response = await createSector(updatedSectorData);
          if (response) {
            await fetchSectors();
            addToast('Setor criado com sucesso', 'success');
            handleCloseDrawer();

            // Disparar evento para notificar que os setores foram atualizados
            window.dispatchEvent(new CustomEvent('sectorsUpdated'));
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          addToast(errorMessage || 'Erro ao criar setor', 'error');
        }
      }
    } catch (error) {
      addToast('Erro ao processar operação', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    setIsDeleting(prev => ({ ...prev, [id]: true }));
    try {
      await deleteSector(id);
      setSectors(sectors.filter(sector => sector.id !== id));
      addToast('Setor excluído com sucesso', 'success');

      // Disparar evento para notificar que os setores foram atualizados
      window.dispatchEvent(new CustomEvent('sectorsUpdated'));
    } catch (error) {
      addToast('Erro ao excluir setor', 'error');
    } finally {
      setIsDeleting(prev => ({ ...prev, [id]: false }));
      setConfirmDeleteId(null);
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingSector(null);
    setSectorData({
      name: '',
      phoneNumberId: '',
      accessToken: '',
      description: '',
      isOfficial: false,
    });
    setErrors({});
    document.body.classList.remove('drawer-open');
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
    document.body.classList.add('drawer-open');
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCloseDrawer();
    }
  };

  // Função para buscar QR code
  const fetchQRCode = async (sectorId: number) => {
    try {
      setIsLoadingQRCode(true);
      const sector = sectors.find(s => s.id === sectorId);
      if (!sector) return;

      if (!sector.isOfficial) {
        const status = await getWhatsAppStatus(sectorId, false);
        if (status.status === 'connected') {
          addToast('WhatsApp já está conectado neste setor.', 'info');
          setShowQRCode(false);
          setQRCodeData(null);
          setSelectedSectorForQR(null);
          return;
        }
      }

      const response = await getWhatsAppQRCode(sectorId, sector.isOfficial);
      setQRCodeData(response);
    } catch (error) {
      addToast('Erro ao gerar QR Code. Aguarde alguns segundos e tente novamente.', 'error');
    } finally {
      setIsLoadingQRCode(false);
    }
  };

  // Função para mostrar QR code
  const handleShowQRCode = async (sectorId: number) => {
    setSelectedSectorForQR(sectorId);
    setShowQRCode(true);
    await fetchQRCode(sectorId);
  };

  // Função para fechar QR code
  const handleCloseQRCode = () => {
    setShowQRCode(false);
    setQRCodeData(null);
    setSelectedSectorForQR(null);
    if (statusPollingInterval) {
      clearInterval(statusPollingInterval);
      setStatusPollingInterval(null);
    }
  };

  // Renderizar status do WhatsApp
  const renderWhatsAppStatus = (sector: Sector) => {
    // Considera conectado apenas se status for explicitamente 'connected'
    const isConnected = sector.whatsappConnection?.status === 'connected';
    const statusColor = isConnected ? '#0C85BA' : '#FF5252';
    const statusText = isConnected ? 'Conectado' : 'Desconectado';

    return (
      <div className="whatsapp-status" style={{ color: statusColor }}>
        <span className="status-dot" style={{ backgroundColor: statusColor }} />
        <span>{statusText}</span>
      </div>
    );
  };



  // Atualizar o formulário para incluir isOfficial
  const renderForm = () => (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <div className="form-group">
        <label>Nome do Setor <span className="required">*</span></label>
        <input
          type="text"
          className={`form-input ${errors.name ? 'error' : ''}`}
          value={sectorData.name}
          placeholder="Ex: Vendas, Suporte, Marketing..."
          onChange={(e) => {
            setSectorData({ ...sectorData, name: e.target.value });
            if (errors.name) setErrors({ ...errors, name: '' });
          }}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      <div className="form-group">
        <label>Descrição <span className="required">*</span></label>
        <textarea
          className={`form-input form-textarea ${errors.description ? 'error' : ''}`}
          value={sectorData.description}
          placeholder="Descreva o propósito ou função deste setor..."
          onChange={(e) => {
            setSectorData({ ...sectorData, description: e.target.value });
            if (errors.description) setErrors({ ...errors, description: '' });
          }}
        />
        {errors.description && <span className="error-message">{errors.description}</span>}
      </div>

      <div className="drawer-footer">
        <button type="button" className="btn btn-secondary" onClick={handleCloseDrawer} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading && (
            <FiLoader className="spin" style={{ marginRight: 8 }} />
          )}
          {editingSector ? 'Salvar' : 'Criar'}
        </button>
      </div>
    </form>
  );

  const filteredSectors = useMemo(() => {
    return sectors.filter(s => {
      const matchSearch = !searchFilter.trim() ||
        s.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (s.description || '').toLowerCase().includes(searchFilter.toLowerCase());
      const matchWa = waFilter === 'all' || s.isOfficial ||
        (waFilter === 'connected' && s.whatsappConnection?.status === 'connected') ||
        (waFilter === 'disconnected' && s.whatsappConnection?.status !== 'connected');
      return matchSearch && matchWa;
    });
  }, [sectors, searchFilter, waFilter]);

  const paginatedSectors = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return filteredSectors.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSectors, currentPage]);

  const totalPages = Math.ceil(filteredSectors.length / ITEMS_PER_PAGE);

  const handleEditSector = (sector: Sector) => {
    setEditingSector(sector);
    setSectorData({
      name: sector.name,
      description: sector.description,
      isOfficial: sector.isOfficial,
      phoneNumberId: sector.phoneNumberId || '',
      accessToken: sector.accessToken || '',
    });
    handleOpenDrawer();
  };

  return (
    <div className="sectors-screen">
      <div className="sectors-header">
        <div className="header-content">
          <h1>Gerenciar Setores</h1>
          <p className="header-description">Gerencie os setores da sua empresa</p>
        </div>
        {canManageSectors && (
          <button
            className="add-button"
            onClick={handleOpenDrawer}
          >
            <FiPlus />
            Novo Setor
          </button>
        )}
      </div>

      <div className="sectors-content">
        {isLoading ? (
          <div className="loading-overlay">
            <div className="card-loading">
              <div className="card-loading-spinner" />
              <span className="loading-text">Carregando setores...</span>
            </div>
          </div>
        ) : sectors.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <FiFolder size={48} />
            </div>
            <h3>Nenhum setor cadastrado</h3>
            <p>Clique no botão "Novo Setor" para começar</p>
          </div>
        ) : (
          <>
            <div className="sectors-filters">
              <input
                type="text"
                className="sectors-filter-search"
                placeholder="Buscar por nome ou descrição..."
                value={searchFilter}
                onChange={(e) => { setSearchFilter(e.target.value); setCurrentPage(0); }}
              />
              <select
                className="sectors-filter-select"
                value={waFilter}
                onChange={(e) => { setWaFilter(e.target.value as 'all' | 'connected' | 'disconnected'); setCurrentPage(0); }}
              >
                <option value="all">WhatsApp: Todos</option>
                <option value="connected">Conectado</option>
                <option value="disconnected">Desconectado</option>
              </select>
            </div>
            {filteredSectors.length > 0 ? (
              <div className="sectors-list-container">
                <table className="sectors-list-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th>WhatsApp</th>
                      {canManageSectors && <th className="sectors-list-actions-col">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSectors.map((sector) => (
                      <tr key={sector.id} className={confirmDeleteId === sector.id ? 'sectors-list-row-deleting' : ''}>
                        {confirmDeleteId === sector.id ? (
                          <td colSpan={canManageSectors ? 4 : 3} className="sectors-delete-confirm-cell">
                            <div className="sectors-delete-confirm-inline">
                              <div className="delete-message-container">
                                <h4 className="delete-title">Confirmar Exclusão</h4>
                                <p className="delete-message">
                                  Tem certeza que deseja excluir o setor "{sector.name}"? Esta ação não poderá ser desfeita.
                                </p>
                              </div>
                              <div className="confirmation-actions">
                                <button className="cancel-button" onClick={() => setConfirmDeleteId(null)} disabled={isDeleting[sector.id!]}>
                                  <FiX /> Cancelar
                                </button>
                                <button className="confirm-button" onClick={() => handleDelete(sector.id!)} disabled={isDeleting[sector.id!]}>
                                  {isDeleting[sector.id!] && <FiLoader className="spin" style={{ marginRight: 8 }} />}
                                  Confirmar
                                </button>
                              </div>
                            </div>
                          </td>
                        ) : (
                          <>
                            <td><span className="sectors-list-name">{sector.name}</span></td>
                            <td><span className="sectors-list-desc">{sector.description}</span></td>

                            <td>
                              {sector.isOfficial ? (
                                <span className="sectors-list-wa-na">—</span>
                              ) : (
                                renderWhatsAppStatus(sector)
                              )}
                            </td>
                            {canManageSectors && (
                              <td className="sectors-list-actions-cell">
                                {(sector.roleNoSetor ?? 1) >= 2 ? (
                                  <div className="sector-actions">
                                    {!sector.isOfficial && (
                                      <button
                                        className="action-button whatsapp"
                                        onClick={() => sector.id && handleShowQRCode(sector.id)}
                                        title={sector.whatsappConnection?.status === 'connected' ? 'Reconectar WhatsApp' : 'Conectar WhatsApp'}
                                      >
                                        {sector.whatsappConnection?.status === 'connected' ? 'Reconectar' : 'Conectar'}
                                      </button>
                                    )}
                                    <button className="action-button edit" onClick={() => handleEditSector(sector)} title="Editar">
                                      <FiEdit2 />
                                    </button>
                                    <button className="action-button delete" onClick={() => setConfirmDeleteId(sector.id!)} title="Excluir">
                                      <FiTrash2 />
                                    </button>
                                  </div>
                                ) : (
                                  <span className="sectors-list-wa-na">—</span>
                                )}
                              </td>
                            )}
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="sectors-empty-filtered">
                <p>Nenhum setor encontrado com os filtros aplicados</p>
              </div>
            )}
            {filteredSectors.length > 0 && totalPages > 1 && (
              <div className="sectors-pagination">
                <button
                  className="sectors-pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  Anterior
                </button>
                <span className="sectors-pagination-info">
                  Página {currentPage + 1} de {totalPages} ({filteredSectors.length} resultado{filteredSectors.length !== 1 ? 's' : ''})
                </span>
                <button
                  className="sectors-pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal do QR Code */}
      {showQRCode && (
        <div className="qrcode-modal-overlay" onClick={handleCloseQRCode}>
          <div className="qrcode-modal" onClick={e => e.stopPropagation()}>
            <div className="qrcode-modal-header">
              <h3>Conectar WhatsApp</h3>
              <button className="close-button" onClick={handleCloseQRCode}>
                <FiX />
              </button>
            </div>
            <div className="qrcode-modal-content">
              {whatsappStatus && (
                <div className={`whatsapp-status-message ${whatsappStatus.status}`}>
                  {whatsappStatus.message}
                </div>
              )}
              {isLoadingQRCode ? (
                <div className="qrcode-loading">
                  <div className="qrcode-loading-spinner" />
                  <span className="qrcode-loading-text">Gerando QR Code...</span>
                </div>
              ) : (
                <>
                  {qrCodeData && whatsappStatus?.status !== 'connected' && (
                    <div className="qrcode-container">
                      <img src={qrCodeData.qrcode} alt="QR Code para conexão do WhatsApp" />
                    </div>
                  )}
                  {whatsappStatus?.status === 'connected' && (
                    <div className="connection-success">
                      <FiCheckCircle size={48} />
                      <p>WhatsApp conectado com sucesso!</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isDrawerOpen && (
        <div className="drawer-overlay" onClick={handleOverlayClick}>
          <div className="sector-drawer">
            <div className="drawer-header">
              <h2>{editingSector ? 'Editar Setor' : 'Novo Setor'}</h2>
              <button className="close-button" onClick={handleCloseDrawer}>
                <FiX />
              </button>
            </div>

            <div className="drawer-content">
              {renderForm()}
            </div>
          </div>
        </div>
      )}

      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  );
};

export default SectorsPage;
